import { useState } from "react";
import {
  AccessDeniedState,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Dialog,
  EntitlementDeniedState,
  Field,
  SeparationOfDutiesState,
  StudioState,
  Select,
  TextArea,
  TextInput,
} from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import { api, documentsApi, meetingApi } from "../../lib/api";
import type {
  Meeting,
  MeetingParticipantInput,
  MeetingParticipantRole,
  MeetingState,
} from "@odingard/sdk-client";

const PRECONDITION_TITLES: Record<string, string> = {
  extraction_source_required: "Pin a source before running extraction",
  adoption_requires_confirmed_facts: "Confirm a decision or commitment before adoption",
  meeting_state_precondition: "This action is unavailable in the current meeting state",
  meeting_transition_not_allowed: "That lifecycle transition is not available",
  confirmation_attendance_required: "Confirm attendance before recording this fact",
  minutes_already_sealed: "These meeting minutes are already sealed",
  completion_evidence_mismatch: "Completion evidence no longer matches",
  governed_endpoint_required: "Use the governed lifecycle action for this change",
  commitment_transition_not_allowed: "That commitment state change is not available",
  commitment_change_precondition: "This commitment cannot be changed in its current state",
};

const LEGACY_PRECONDITION_MESSAGES = new Set([
  "Extraction can only run while the meeting is DRAFT or IN_REVIEW.",
  "Extraction requires at least one active meeting source.",
  "Candidate disposition can only change while the meeting is DRAFT or IN_REVIEW.",
  "Confirmation is only legal while the meeting is DRAFT or IN_REVIEW.",
  "Confirmation requires current internal attendance or an override reason.",
  "An attendance override requires a reason.",
  "Meeting fields and participation can only change in DRAFT or IN_REVIEW.",
  "Meeting sources can only change in DRAFT or IN_REVIEW.",
  "Only ADOPTED meetings can be sealed.",
  "Meeting minutes already exist.",
  "Completion evidence checksum does not match the pinned Documents version.",
  "ADOPTED and SEALED transitions require the governed adoption and sealing endpoints.",
  "Only IN_REVIEW meetings can be adopted.",
]);

function preconditionTitle(error: ApiError): string | null {
  const knownTitle = PRECONDITION_TITLES[error.code];
  if (knownTitle) return knownTitle;
  if (
    error.code === "conflict" &&
    (LEGACY_PRECONDITION_MESSAGES.has(error.message) ||
      error.message.startsWith("Illegal meeting transition from ") ||
      error.message.startsWith("Illegal commitment transition from ") ||
      error.message.startsWith("Terminal commitments cannot be changed") ||
      error.message.startsWith("Commitment changes require a rationale") ||
      error.message.startsWith("Commitment due-date changes require a rationale"))
  ) {
    if (error.message === "Extraction requires at least one active meeting source.") {
      return PRECONDITION_TITLES.extraction_source_required ?? null;
    }
    return "This prerequisite has not been met";
  }
  return null;
}

function preconditionDescription(error: ApiError): string {
  if (
    error.code === "extraction_source_required" ||
    (error.code === "conflict" && error.message === "Extraction requires at least one active meeting source.")
  ) {
    return "This meeting needs at least one active pinned source before extraction can run.";
  }
  return error.message;
}

export function Refusal({ error }: { error: unknown }) {
  if (typeof error === "string") return <StudioState state="error" description={error} />;
  if (!(error instanceof ApiError)) return <StudioState state="error" description="Meeting AI could not complete this governed action." />;
  if (error.code === "entitlement_denied") return <EntitlementDeniedState product="Meeting AI" />;
  if (error.code === "separation_of_duties") {
    return (
      <SeparationOfDutiesState
        action="Adopt minutes"
        reason={error.message}
        whoCanAct="A different meeting participant must adopt these minutes."
      />
    );
  }
  const title = preconditionTitle(error);
  if (title) {
    return <StudioState state="error" title={title} description={preconditionDescription(error)} />;
  }
  if (error.code === "forbidden" || error.status === 403) {
    const permission = typeof error.details?.permission === "string" ? error.details.permission : null;
    return <AccessDeniedState reason={permission ? `This action requires ${permission}. ${error.message}` : error.message} />;
  }
  if (error.code === "conflict") {
    return <StudioState state="concurrent_state_conflict" description={error.message} />;
  }
  if (error.code === "unsupported_media_type") {
    return <StudioState state="unsupported" description={error.message} />;
  }
  if (error.code === "documents_access_denied") {
    return <StudioState state="unauthorized" description={error.message} />;
  }
  return <StudioState state="error" description={error.message} />;
}

function ActionButton({ children, onClick, disabled }: { permission?: string; permissions?: string[]; children: string; onClick: () => void; disabled?: boolean }) {
  return <Button size="sm" disabled={disabled} onClick={onClick}>{children}</Button>;
}

export function CreateMeeting({ orgId, permissions: _permissions, onCreated }: { orgId: string; permissions?: string[]; onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [purpose, setPurpose] = useState("");
  const [scheduledStart, setScheduledStart] = useState("");
  const [classification, setClassification] = useState<"public" | "internal" | "confidential" | "restricted">("internal");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function submit() {
    setBusy(true); setError(null);
    try {
      await meetingApi.createMeeting(orgId, {
        title, purpose, kind: "internal", scheduledStart: new Date(scheduledStart).toISOString(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, confidentialityClassification: classification,
      });
      setTitle(""); setPurpose(""); onCreated();
    } catch (cause) { setError(cause); } finally { setBusy(false); }
  }
  return (
    <>
      <Button size="sm" aria-expanded={open} aria-controls="create-governed-meeting" onClick={() => setOpen((current) => !current)}>Create meeting</Button>
      <Dialog open={open} title="Create governed meeting" onClose={() => setOpen(false)}>
        <div id="create-governed-meeting">
        <Field label="Meeting title">{(id) => <TextInput id={id} value={title} onChange={(event) => setTitle(event.target.value)} />}</Field>
        <Field label="Purpose">{(id) => <TextArea id={id} value={purpose} onChange={(event) => setPurpose(event.target.value)} />}</Field>
        <Field label="Meeting time">{(id) => <TextInput id={id} type="datetime-local" value={scheduledStart} onChange={(event) => setScheduledStart(event.target.value)} />}</Field>
        <Field label="Confidentiality classification">{(id) => <Select id={id} value={classification} onChange={(event) => setClassification(event.target.value as typeof classification)}><option value="public">Public</option><option value="internal">Internal</option><option value="confidential">Confidential</option><option value="restricted">Restricted</option></Select>}</Field>
        <ActionButton permission="meetings.manage" onClick={() => void submit()}>Create meeting</ActionButton>
        {busy && <p role="status">Recording meeting…</p>}
        {error !== null && <Refusal error={error} />}
        </div>
      </Dialog>
    </>
  );
}

export function MeetingLifecycleActions({ orgId, meeting, onChanged }: { orgId: string; meeting: Meeting; onChanged: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState<null | { label: string; action: () => Promise<unknown> }>(null);
  const transition = (targetState: MeetingState, label: string) => setPending({ label, action: () => meetingApi.transition(orgId, meeting.id, { targetState, reason }) });
  const run = async () => {
    if (!pending) return;
    setBusy(true); setError(null);
    try { await pending.action(); setPending(null); setReason(""); onChanged(); }
    catch (cause) { setError(cause); }
    finally { setBusy(false); }
  };
  const actions: { label: string; onClick: () => void }[] = [];
  if (meeting.state === "DRAFT") {
    actions.push({ label: "Move to in review", onClick: () => transition("IN_REVIEW", "Move to in review") });
    actions.push({ label: "Abandon meeting", onClick: () => transition("ABANDONED", "Abandon meeting") });
  }
  if (meeting.state === "IN_REVIEW") {
    actions.push({ label: "Abandon meeting", onClick: () => transition("ABANDONED", "Abandon meeting") });
    actions.push({ label: "Adopt minutes", onClick: () => setPending({ label: "Adopt minutes", action: () => meetingApi.adopt(orgId, meeting.id, reason) }) });
  }
  if (meeting.state === "ADOPTED") {
    actions.push({ label: "Seal minutes", onClick: () => setPending({ label: "Seal minutes", action: () => meetingApi.seal(orgId, meeting.id, reason) }) });
  }
  return (
    <Card>
      <CardHeader title="Lifecycle governance" />
      <CardBody>
        <p>State changes are append-only governed actions and require a rationale.</p>
        {actions.length > 0 && <TextArea aria-label="Lifecycle rationale" placeholder="Rationale" value={reason} onChange={(event) => setReason(event.target.value)} />}
        <div className="ods-row">{actions.map((action) => <ActionButton key={action.label} onClick={action.onClick}>{action.label}</ActionButton>)}</div>
        {busy && <p role="status">Applying lifecycle action…</p>}
        {error !== null && <Refusal error={error} />}
      </CardBody>
      <ConfirmDialog open={pending !== null} title={`Confirm ${pending?.label ?? "lifecycle action"}`} description="This governed lifecycle action is recorded in the meeting audit history." confirmLabel="Confirm" busy={busy} disabled={!reason.trim()} onCancel={() => setPending(null)} onConfirm={() => void run()} />
    </Card>
  );
}

export function MeetingActionPanel({ orgId, meetingId, members = [], permissions: _permissions, onChanged }: { orgId: string; meetingId: string; members?: { id: string; displayName: string; email: string }[]; permissions?: string[]; onChanged: () => void }) {
  const [participantName, setParticipantName] = useState("");
  const [participantEmail, setParticipantEmail] = useState("");
  const [participantKind, setParticipantKind] = useState<"internal" | "external">("internal");
  const [participantUserId, setParticipantUserId] = useState("");
  const [participantRole, setParticipantRole] = useState<MeetingParticipantRole>("participant");
  const [documentId, setDocumentId] = useState("");
  const [documents, setDocuments] = useState<{ id: string; displayName: string; filename: string }[]>([]);
  const [sourceId, setSourceId] = useState("");
  const [detachReason, setDetachReason] = useState("");
  const [manualDecision, setManualDecision] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [confirm, setConfirm] = useState<null | (() => Promise<void>)>(null);
  async function loadDocuments() {
    try { setDocuments((await documentsApi.list(orgId, { status: "active" })).documents); }
    catch (cause) { setError(cause); }
  }
  async function run(action: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await action(); onChanged(); } catch (cause) { setError(cause); } finally { setBusy(false); setConfirm(null); }
  }
  return (
    <Card>
      <CardHeader title="Governed actions" />
      <CardBody>
        <div className="ods-stack">
          <strong>Record attendee</strong>
          <Select aria-label="Attendee type" value={participantKind} onChange={(event) => setParticipantKind(event.target.value as typeof participantKind)}><option value="internal">Internal Studio member</option><option value="external">External attendee</option></Select>
          <Select aria-label="Attendee role" value={participantRole} onChange={(event) => setParticipantRole(event.target.value as MeetingParticipantRole)}><option value="chair">Chair</option><option value="minute_taker">Minute taker</option><option value="participant">Participant</option><option value="apology">Apology</option></Select>
          {participantKind === "internal" ? (
            <Select aria-label="Internal attendee" value={participantUserId} onChange={(event) => setParticipantUserId(event.target.value)}>
              <option value="">Choose an organization member</option>
              {members.map((member) => <option key={member.id} value={member.id}>{member.displayName}</option>)}
            </Select>
          ) : (
            <>
              <TextInput aria-label="External attendee name" placeholder="Name" value={participantName} onChange={(event) => setParticipantName(event.target.value)} />
              <TextInput aria-label="External attendee email" placeholder="Email" type="email" value={participantEmail} onChange={(event) => setParticipantEmail(event.target.value)} />
            </>
          )}
          <ActionButton
            permission="meetings.manage"
            disabled={participantKind === "internal" ? !participantUserId : !participantName.trim() || !participantEmail.trim()}
            onClick={() => {
              const input: MeetingParticipantInput = participantKind === "internal"
                ? { participantKind, userId: participantUserId, role: participantRole }
                : { participantKind, externalName: participantName.trim(), externalEmail: participantEmail.trim(), role: participantRole };
              void run(() => meetingApi.addParticipant(orgId, meetingId, input));
            }}
          >
            Add attendee
          </ActionButton>
        </div>
        <div className="ods-stack">
          <strong>Documents source</strong>
          <Button size="sm" variant="secondary" onClick={() => void loadDocuments()}>Load Documents</Button>
          <Select aria-label="Meeting source document" value={documentId} onChange={(event) => setDocumentId(event.target.value)}><option value="">Choose a document</option>{documents.map((document) => <option key={document.id} value={document.id}>{document.displayName || document.filename}</option>)}</Select>
          <p>Pinning uses the latest Documents version returned for the selected document.</p>
          <details><summary>Manual source reference fallback</summary><TextInput aria-label="Documents document ID" placeholder="Document ID" value={documentId} onChange={(event) => setDocumentId(event.target.value)} /><p>Fallback for a caller-readable Documents reference the picker cannot cover.</p></details>
          <ActionButton permission="meetings.manage" onClick={() => void run(() => meetingApi.attachSource(orgId, meetingId, { documentId }))}>Attach source</ActionButton>
          <TextInput aria-label="Meeting source ID" placeholder="Attached source ID to detach" value={sourceId} onChange={(event) => setSourceId(event.target.value)} />
          <TextArea aria-label="Source detachment reason" placeholder="Reason for detaching source" value={detachReason} onChange={(event) => setDetachReason(event.target.value)} />
          <ActionButton permission="meetings.manage" disabled={!sourceId.trim() || !detachReason.trim()} onClick={() => void run(() => meetingApi.detachSource(orgId, meetingId, sourceId, detachReason.trim()))}>Detach source</ActionButton>
          <p>Only caller-readable Documents references are accepted. Audio/video and inaccessible documents remain explicit refusals.</p>
        </div>
        <div className="ods-stack">
          <strong>Extraction</strong>
          <ActionButton permission="meetings.manage" onClick={() => void run(() => meetingApi.runExtraction(orgId, meetingId))}>Run extraction</ActionButton>
        </div>
        <div className="ods-stack">
          <strong>Record decision by hand</strong>
          <TextArea aria-label="Manual decision" value={manualDecision} onChange={(event) => setManualDecision(event.target.value)} placeholder="Decision statement" />
          <ActionButton permission="meetings.confirm" onClick={() => setConfirm(() => () => meetingApi.confirmDecision(orgId, meetingId, { statement: manualDecision }).then(() => undefined))}>Record decision</ActionButton>
        </div>
        {busy && <p role="status">Applying governed action…</p>}
        {error !== null && <Refusal error={error} />}
      </CardBody>
      <ConfirmDialog open={confirm !== null} title="Confirm governed action" description="This action creates an append-only governed record and cannot be undone by the proxy." confirmLabel="Confirm" busy={busy} onCancel={() => setConfirm(null)} onConfirm={() => confirm ? void run(confirm) : undefined} />
    </Card>
  );
}

export function CommitmentActions({ orgId, commitmentId, permissions: _permissions, onChanged }: { orgId: string; commitmentId: string; permissions?: string[]; onChanged: () => void }) {
  const [owner, setOwner] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [noDueDate, setNoDueDate] = useState(false);
  const [rationale, setRationale] = useState("");
  const [evidence, setEvidence] = useState("");
  const [evidenceVersion, setEvidenceVersion] = useState("");
  const [evidenceHash, setEvidenceHash] = useState("");
  const [documents, setDocuments] = useState<{ id: string; displayName: string; filename: string }[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<{ documentId: string; version: number; checksum: string; filename?: string } | null>(null);
  const [members, setMembers] = useState<{ id: string; displayName: string }[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const loadMembers = async () => { const response = await api.members(orgId); setMembers(response.members); };
  async function loadDocuments() {
    try { setDocuments((await documentsApi.list(orgId, { status: "active" })).documents); }
    catch (cause) { setError(cause); }
  }
  async function selectDocument(documentId: string) {
    if (!documentId) { setSelectedDocument(null); return; }
    try {
      const response = await documentsApi.get(orgId, documentId);
      setSelectedDocument({ documentId: response.document.id, version: response.document.latestVersion, checksum: response.document.checksum, filename: response.document.filename });
    } catch (cause) { setError(cause); setSelectedDocument(null); }
  }
  async function run(action: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await action(); onChanged(); } catch (cause) { setError(cause); } finally { setBusy(false); }
  }
  const validDueDate = noDueDate || Boolean(dueDate);
  return (
    <Card>
      <CardHeader title="Commitment controls" />
      <CardBody>
        <Button size="sm" variant="secondary" onClick={() => void loadMembers()}>Load organization members</Button>
        <Field label="Owner">{(id) => <Select id={id} value={owner} onChange={(event) => setOwner(event.target.value)}><option value="">Choose an owner</option>{members.map((member) => <option key={member.id} value={member.id}>{member.displayName}</option>)}</Select>}</Field>
        <Field label="Due date">{(id) => <TextInput id={id} type="date" value={dueDate} disabled={noDueDate} onChange={(event) => setDueDate(event.target.value)} />}</Field>
        <label><input type="checkbox" checked={noDueDate} onChange={(event) => setNoDueDate(event.target.checked)} /> Explicitly no due date</label>
        <TextArea aria-label="Commitment rationale" placeholder="Rationale" value={rationale} onChange={(event) => setRationale(event.target.value)} />
        <Button size="sm" variant="secondary" onClick={() => void loadDocuments()}>Load Documents for evidence</Button>
        <Select aria-label="Completion evidence document" value={selectedDocument?.documentId ?? ""} onChange={(event) => void selectDocument(event.target.value)}><option value="">Choose completion evidence</option>{documents.map((document) => <option key={document.id} value={document.id}>{document.displayName || document.filename}</option>)}</Select>
        <p>{selectedDocument ? `Documents returned version ${selectedDocument.version} and checksum ${selectedDocument.checksum}` : "Select a caller-readable Documents artifact."}</p>
        <details><summary>Manual reference fallback</summary><TextInput aria-label="Completion evidence document ID" placeholder="Document ID" value={evidence} onChange={(event) => setEvidence(event.target.value)} /><TextInput aria-label="Completion evidence version" placeholder="Documents version" value={evidenceVersion} onChange={(event) => setEvidenceVersion(event.target.value)} /><TextInput aria-label="Completion evidence hash" placeholder="Documents content hash" value={evidenceHash} onChange={(event) => setEvidenceHash(event.target.value)} /><p>Fallback for references the picker cannot cover; values must come from Documents.</p></details>
        <div className="ods-row">
          <ActionButton permission="meetings.confirm" onClick={() => void run(() => meetingApi.changeOwner(orgId, commitmentId, owner, rationale))}>Change owner</ActionButton>
          <ActionButton permission="meetings.confirm" onClick={() => void run(() => meetingApi.changeDueDate(orgId, commitmentId, noDueDate ? null : dueDate, rationale))}>Change due date</ActionButton>
          <ActionButton permission="meetings.confirm" onClick={() => void run(() => meetingApi.completeCommitment(orgId, commitmentId, { rationale, ...((selectedDocument ?? (evidence ? { documentId: evidence, version: Number(evidenceVersion), checksum: evidenceHash } : null)) ? { evidence: selectedDocument ?? { documentId: evidence, version: Number(evidenceVersion), checksum: evidenceHash } } : {}) }))}>Complete</ActionButton>
          <ActionButton permission="meetings.confirm" onClick={() => void run(() => meetingApi.cancelCommitment(orgId, commitmentId, rationale))}>Cancel</ActionButton>
        </div>
        {busy && <p role="status">Applying commitment action…</p>}
        {!owner || !validDueDate ? <p role="status">Confirmation requires an organization member owner and either a due date or an explicit no-due-date declaration.</p> : null}
        {error !== null && <Refusal error={error} />}
      </CardBody>
    </Card>
  );
}
